/**
 * weaponTable — the per-weapon damage/kills table.
 *
 * One renderer for two callers: the account screen's lifetime figures and the
 * end-of-match recap's this-match figures. Both arrays arrive in the same
 * shape ({ weaponId, name, damageDealt, kills }) already sorted by damage with
 * a server-resolved name, so this never sorts and never looks a name up.
 * Pure DOM out, no fetching and no state.
 */

const COLUMNS = ['Weapon', 'Damage', 'Kills'];

/**
 * @param {Array<{weaponId: string, name: string, damageDealt: number, kills: number}>} weapons
 * @param {{emptyMessage?: string}} [options]
 * @returns {HTMLElement} a wrapper holding either the table or an empty-state line
 */
export function renderWeaponTable(weapons, { emptyMessage = 'No weapon damage yet.' } = {}) {
  const wrap = document.createElement('div');
  wrap.className = 'weapon-table-wrap';

  const rows = Array.isArray(weapons) ? weapons : [];
  if (rows.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'stat-empty';
    empty.textContent = emptyMessage;
    wrap.appendChild(empty);
    return wrap;
  }

  const table = document.createElement('table');
  table.className = 'stat-table weapon-table';

  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const label of COLUMNS) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.textContent = label;
    headRow.appendChild(th);
  }
  thead.appendChild(headRow);

  const tbody = document.createElement('tbody');
  for (const w of rows) {
    const tr = document.createElement('tr');
    tr.dataset.weaponId = w.weaponId || '';
    for (const value of [w.name || w.weaponId || '', Math.round(w.damageDealt || 0), w.kills || 0]) {
      const td = document.createElement('td');
      td.textContent = String(value);       // server-resolved copy, rendered as text
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }

  table.append(thead, tbody);
  wrap.appendChild(table);
  return wrap;
}
